import { DatePipe } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AutoCompleteCompleteEvent, AutoCompleteModule } from 'primeng/autocomplete';
import { AvatarModule } from 'primeng/avatar';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { DrawerModule } from 'primeng/drawer';
import { FloatLabelModule } from 'primeng/floatlabel';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TextareaModule } from 'primeng/textarea';
import { Subscription } from 'rxjs';
import { User } from '../../administration/users/user.model';
import { UsersService } from '../../administration/users/users.service';
import { MessagesService } from '../../messages/messages.service';
import { Chat, ChatMember, ChatMessage } from '../../messages/chat.model';
import { ChatRealtimeService } from '../../messages/chat-realtime.service';
import { AuthService } from '../../login/auth.service';
import { AvatarLabelPipe } from '../../shared/pipes/avatar-label.pipe';
import { SidebarService } from '../../shared/sidebar/sidebar.service';

type ChatPanel = 'list' | 'new' | 'chat';

@Component({
  selector: 'app-pickup-chat',
  imports: [
    AutoCompleteModule,
    AvatarLabelPipe,
    AvatarModule,
    BadgeModule,
    ButtonModule,
    DatePipe,
    DrawerModule,
    FloatLabelModule,
    ProgressSpinnerModule,
    ReactiveFormsModule,
    TextareaModule,
  ],
  templateUrl: './pickup-chat.component.html',
  styleUrl: './pickup-chat.component.scss',
  standalone: true,
})
export class PickupChatComponent implements OnInit, OnDestroy {
  @ViewChild('pickupMessagesEnd') pickupMessagesEnd?: ElementRef<HTMLElement>;

  private messagesService = inject(MessagesService);
  private authService = inject(AuthService);
  private chatRealtimeService = inject(ChatRealtimeService);
  private sidebarService = inject(SidebarService);
  private usersService = inject(UsersService);

  visible = false;
  panel: ChatPanel = 'list';
  chats: Chat[] = [];
  selectedChat: Chat | null = null;
  users: User[] = [];
  loading = true;
  sending = false;

  newChatForm = new FormGroup({
    recipients: new FormControl<User[]>([], [Validators.required]),
  });

  replyForm = new FormGroup({
    message: new FormControl<string>('', [Validators.required]),
  });

  private realtimeSubscription?: Subscription;

  async ngOnInit(): Promise<void> {
    if (this.authService.user() === null) {
      await this.authService.fetchMe();
    }
    await this.loadUsers();
    await this.loadChats();
    this.realtimeSubscription = this.chatRealtimeService.messageReceived.subscribe(() => {
      void this.loadChats();
    });
  }

  ngOnDestroy(): void {
    this.realtimeSubscription?.unsubscribe();
  }

  async open(): Promise<void> {
    this.visible = true;
    this.panel = this.selectedChat ? 'chat' : 'list';
    await this.loadChats();
    if (this.panel === 'chat') {
      this.scrollToLatestMessage();
    }
  }

  close(): void {
    this.visible = false;
  }

  showList(): void {
    this.panel = 'list';
    this.selectedChat = null;
  }

  showNewChat(): void {
    this.newChatForm.reset({ recipients: [] });
    this.panel = 'new';
  }

  async createChat(): Promise<void> {
    if (this.newChatForm.invalid) return;

    this.sending = true;
    try {
      const { recipients } = this.newChatForm.getRawValue();
      const chat = await this.messagesService.createChat(recipients ?? []);
      this.newChatForm.reset({ recipients: [] });
      await this.loadChats();
      this.selectedChat = this.chats.find((entry) => entry.id === chat.id) ?? chat;
      this.panel = 'chat';
      this.scrollToLatestMessage();
    } finally {
      this.sending = false;
    }
  }

  async selectChat(chat: Chat): Promise<void> {
    this.selectedChat = chat;
    this.panel = 'chat';
    await this.markSelectedChatAsRead();
    this.scrollToLatestMessage();
  }

  async sendMessage(): Promise<void> {
    if (!this.selectedChat || this.replyForm.invalid) return;

    this.sending = true;
    try {
      const message = this.replyForm.getRawValue().message ?? '';
      await this.messagesService.createChatMessage(this.selectedChat.id, message);
      this.replyForm.reset({ message: '' });
      await this.loadChats();
    } finally {
      this.sending = false;
    }
  }

  handleReplyKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' || event.shiftKey) return;

    event.preventDefault();
    void this.sendMessage();
  }

  filterUsers(event: AutoCompleteCompleteEvent): void {
    const query = event.query.trim().toLowerCase();
    this.users = this.availableUsers().filter((user) =>
      user.first_name?.trim().toLowerCase().includes(query) ||
      user.last_name?.trim().toLowerCase().includes(query) ||
      user.username.trim().toLowerCase().includes(query));
  }

  chatTitle(chat: Chat | null): string {
    if (!chat) return 'Nachrichten';
    if (chat.name) return chat.name;

    const members = this.otherMembers(chat);
    if (members.length === 0) return 'Nur du';

    return members.map((member) => this.userName(member.user)).join(', ');
  }

  firstOtherUser(chat: Chat): User | undefined {
    return this.otherMembers(chat)[0]?.user;
  }

  lastMessage(chat: Chat): ChatMessage | undefined {
    const messages = this.sortedMessages(chat);
    return messages[messages.length - 1];
  }

  lastMessagePreview(chat: Chat): string {
    const lastMessage = this.lastMessage(chat);
    if (!lastMessage) return 'Noch keine Nachrichten';

    const prefix = this.isOwnMessage(lastMessage) ? 'Du: ' : '';
    return `${prefix}${lastMessage.body ?? ''}`;
  }

  sortedMessages(chat: Chat | null): ChatMessage[] {
    return [...(chat?.chat_messages ?? [])].sort((a, b) => this.messageTime(a) - this.messageTime(b));
  }

  unreadCount(chat: Chat): number {
    const ownMember = this.ownMember(chat);
    const lastReadAt = ownMember?.last_read_at ? new Date(ownMember.last_read_at).getTime() : 0;
    return (chat.chat_messages ?? []).filter((message) =>
      !this.isOwnMessage(message) && this.messageTime(message) > lastReadAt
    ).length;
  }

  unreadTotal(): number {
    return this.chats.map((chat) => this.unreadCount(chat)).reduce((sum, count) => sum + count, 0);
  }

  isOwnMessage(message: ChatMessage): boolean {
    return message.users_permissions_user?.id === this.authService.user()?.id;
  }

  messageDate(message?: ChatMessage): string | null {
    return message?.sent_at ?? message?.createdAt ?? null;
  }

  showDateSeparator(message: ChatMessage, chat: Chat, index: number): boolean {
    if (index === 0) return true;

    const previousMessage = this.sortedMessages(chat)[index - 1];
    return this.messageDayKey(message) !== this.messageDayKey(previousMessage);
  }

  messageReadState(message: ChatMessage, chat: Chat): 'sent' | 'read' | null {
    if (!this.isOwnMessage(message)) return null;

    const recipients = this.otherMembers(chat);
    if (recipients.length === 0) return null;

    const messageTime = this.messageTime(message);
    const readCount = recipients.filter((member) =>
      member.last_read_at != null && new Date(member.last_read_at).getTime() >= messageTime
    ).length;

    return readCount > 0 ? 'read' : 'sent';
  }

  userName(user?: User): string {
    if (!user) return 'Unbekannt';
    const fullName = `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim();
    return fullName.length > 0 ? fullName : user.username;
  }

  private async loadChats(): Promise<void> {
    this.loading = true;
    try {
      const selectedChatId = this.selectedChat?.id;
      this.chats = this.sortChats(await this.messagesService.fetchMyChats());
      if (selectedChatId) {
        this.selectedChat = this.chats.find((chat) => chat.id === selectedChatId) ?? null;
      }
      if (this.shouldMarkSelectedChatAsRead()) {
        await this.markSelectedChatAsRead();
        this.scrollToLatestMessage();
      }
    } finally {
      this.loading = false;
    }
  }

  private async loadUsers(): Promise<void> {
    await this.usersService.fetchUsers();
    this.users = this.availableUsers();
  }

  private async markSelectedChatAsRead(): Promise<void> {
    if (!this.selectedChat) return;

    await this.messagesService.markChatAsRead(this.selectedChat.id);
    const ownMember = this.ownMember(this.selectedChat);
    if (ownMember) {
      ownMember.last_read_at = new Date().toISOString();
    }
    await this.sidebarService.updateSidebarStats();
  }

  private shouldMarkSelectedChatAsRead(): boolean {
    return this.visible && this.panel === 'chat' && this.selectedChat != null;
  }

  private availableUsers(): User[] {
    const currentUserId = this.authService.user()?.id;
    return this.usersService.users.filter((user) => user.id !== currentUserId);
  }

  private otherMembers(chat: Chat): ChatMember[] {
    const currentUserId = this.authService.user()?.id;
    return chat.chat_members?.filter((member) => member.user?.id !== currentUserId) ?? [];
  }

  private ownMember(chat: Chat): ChatMember | undefined {
    const currentUserId = this.authService.user()?.id;
    return chat.chat_members?.find((member) => member.user?.id === currentUserId);
  }

  private sortChats(chats: Chat[]): Chat[] {
    return [...chats].sort((a, b) => this.chatTime(b) - this.chatTime(a));
  }

  private chatTime(chat: Chat): number {
    return this.messageTime(this.lastMessage(chat)) || new Date(chat.updatedAt ?? chat.createdAt ?? 0).getTime();
  }

  private messageTime(message?: ChatMessage): number {
    if (!message) return 0;
    return new Date(message.sent_at ?? message.createdAt ?? 0).getTime();
  }

  private messageDayKey(message?: ChatMessage): string {
    const date = this.messageDate(message);
    return date ? new Date(date).toDateString() : '';
  }

  private scrollToLatestMessage(): void {
    setTimeout(() => {
      this.pickupMessagesEnd?.nativeElement.scrollIntoView({ block: 'end' });
    });
  }
}
